import { observable } from 'mobx';
import {
  model,
  Model,
  _async,
  _await,
  modelFlow,
  objectMap,
  getRoot,
  prop,
  modelAction,
  ModelCreationData,
} from 'mobx-keystone';

import Store from './Store';
import SelfVerificationType from '../models/SelfVerificationType';
import * as api from '../services/api';
import { getError, getSuccess } from '../utils/models';

@model('bpEwells/SelfVerificationTypeStore')
export default class SelfVerificationTypeStore extends Model({
  selfVerificationTypes: prop(() => objectMap<SelfVerificationType>()),
}) {
  @observable
  loading = false;

  getSelfVerificationType = (id: number): SelfVerificationType | undefined => {
    return this.selfVerificationTypes.get(`${id}`);
  };

  getSelfVerificationTypes = (): SelfVerificationType[] => {
    return Array.from(this.selfVerificationTypes.values());
  };

  getActiveSelfVerificationTypes = (): SelfVerificationType[] => {
    return Array.from(this.selfVerificationTypes.values()).filter(
      (s) => s.isActive,
    );
  };

  @modelAction
  createOrUpdateSelfVerificationType(
    data: ModelCreationData<SelfVerificationType>,
  ) {
    const id = `${data.id}`;

    let selfVerificationType: SelfVerificationType;
    if (this.selfVerificationTypes.has(id)) {
      selfVerificationType = this.selfVerificationTypes.get(id)!;
    } else {
      selfVerificationType = new SelfVerificationType(data);
      this.selfVerificationTypes.set(id, selfVerificationType);
    }

    selfVerificationType.update(data);
  }

  @modelFlow
  fetchSelfVerificationTypes = _async(function* (
    this: SelfVerificationTypeStore,
  ) {
    const rootStore = getRoot<Store>(this);

    if (!rootStore.authStore || !rootStore.authStore.accessToken) {
      return getSuccess();
    }

    this.loading = true;

    let results: ModelCreationData<SelfVerificationType>[];
    try {
      ({
        response: {
          entities: { results },
        },
      } = yield* _await(
        api.fetchSelfVerificationTypes(rootStore.authStore.accessToken),
      ));
    } catch (error) {
      console.warn('[DEBUG] error fetching self verification types', error);
      yield* _await(rootStore.authStore.checkToken(error));
      return getError(error);
    }

    results.forEach((data) => this.createOrUpdateSelfVerificationType(data));

    this.loading = false;
    return getSuccess();
  });
}
